import React, { useState } from 'react';
import {
  Container,
  Typography,
  Box,
  Paper,
  Breadcrumbs,
  Link,
  CircularProgress,
  Grid,
  Chip,
  IconButton,
  Tooltip,
  Alert,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Divider,
} from '@mui/material';
import {
  Receipt,
  Home,
  AdminPanelSettings,
  ExpandMore,
  ContentCopy,
  ArrowBack,
  Event,
  Label,
  Code,
  Timeline,
  CheckCircle,
  Error as ErrorIcon,
  SkipNext,
  Info,
  PlayArrow,
} from '@mui/icons-material';
import { useNavigate, useParams, Navigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useCurrentUser } from '../../hooks/useApi';
import { useAuthenticatedFetch } from '../../hooks/useAuthenticatedFetch';
import { API_URL } from '../../utils/config';

interface TraceEntry {
  timestamp: string;
  step: string;
  action: 'start' | 'success' | 'skip' | 'error' | 'info';
  message: string;
  durationMs?: number;
  data?: Record<string, any>;
  error?: { message: string; stack?: string };
}

interface TraceLog {
  traceId: string;
  entityId: string;
  eventType: string;
  startTime: string;
  entries: TraceEntry[];
}

interface WebhookLog {
  id: number;
  eventId: string;
  eventType: string;
  realmId: string;
  entityName: string;
  entityId: string;
  operation: string;
  eventTime: string;
  payload: any;
  processed: boolean;
  processedAt: string;
  errorMessage: string | null;
  createdAt: string;
  processingStatus: 'success' | 'failed' | 'skipped' | 'no_handler' | null;
  traceLog: TraceLog | null;
}

interface WebhookLogResponse {
  success: boolean;
  data: WebhookLog;
}

export const QuickBooksWebhookLogDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const { data: user, isLoading: isLoadingUser } = useCurrentUser();
  const { authenticatedFetch } = useAuthenticatedFetch();
  const navigate = useNavigate();

  const [copiedField, setCopiedField] = useState<string | null>(null);

  // Fetch webhook log detail
  const { data: logData, isLoading } = useQuery<WebhookLogResponse>({
    queryKey: ['quickbooks-webhook-log', id],
    queryFn: async () => {
      if (!id) return null;
      const response = await authenticatedFetch(
        `${API_URL}/api/quickbooks-webhook-logs/portal/logs/${id}`
      );
      if (!response || !response.ok) {
        throw new Error('Failed to fetch webhook log');
      }
      return await response.json();
    },
    enabled: !!id && !!user?.permissions?.includes('permissions.all'),
  });

  // Show loading spinner while user data is being fetched
  if (isLoadingUser) {
    return (
      <Container maxWidth="xl">
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  // Redirect if user doesn't have permissions.all permission
  if (!user?.permissions?.includes('permissions.all')) {
    return <Navigate to="/" replace />;
  }

  const log = logData?.data;

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleString();
  };

  const getOperationColor = (operation: string) => {
    switch (operation) {
      case 'Create':
        return 'success';
      case 'Update':
        return 'info';
      case 'Delete':
        return 'error';
      case 'Void':
        return 'warning';
      default:
        return 'default';
    }
  };

  const getProcessingStatusColor = (status: string | null) => {
    switch (status) {
      case 'success':
        return 'success';
      case 'failed':
        return 'error';
      case 'skipped':
        return 'warning';
      case 'no_handler':
        return 'default';
      default:
        return 'default';
    }
  };

  const getProcessingStatusLabel = (status: string | null) => {
    switch (status) {
      case 'success':
        return 'Success';
      case 'failed':
        return 'Failed';
      case 'skipped':
        return 'Skipped';
      case 'no_handler':
        return 'No Handler';
      default:
        return 'Unknown';
    }
  };

  const getTraceActionIcon = (action: TraceEntry['action']) => {
    switch (action) {
      case 'start':
        return <PlayArrow sx={{ fontSize: 18, color: '#2196f3' }} />;
      case 'success':
        return <CheckCircle sx={{ fontSize: 18, color: '#4caf50' }} />;
      case 'skip':
        return <SkipNext sx={{ fontSize: 18, color: '#ff9800' }} />;
      case 'error':
        return <ErrorIcon sx={{ fontSize: 18, color: '#f44336' }} />;
      case 'info':
        return <Info sx={{ fontSize: 18, color: '#9e9e9e' }} />;
      default:
        return <Info sx={{ fontSize: 18 }} />;
    }
  };

  const getTraceActionColor = (action: TraceEntry['action']) => {
    switch (action) {
      case 'start':
        return '#2196f3';
      case 'success':
        return '#4caf50';
      case 'skip':
        return '#ff9800';
      case 'error':
        return '#f44336';
      case 'info':
        return '#9e9e9e';
      default:
        return '#9e9e9e';
    }
  };

  const formatDuration = (ms: number | undefined) => {
    if (ms === undefined) return '';
    if (ms < 1000) return `${ms}ms`;
    return `${(ms / 1000).toFixed(2)}s`;
  };

  const getTraceSummary = (traceLog: TraceLog | null) => {
    if (!traceLog?.entries) return null;
    const entries = traceLog.entries;
    const totalSteps = entries.filter(e => e.action === 'start' || e.action === 'success').length;
    const errors = entries.filter(e => e.action === 'error').length;
    const skipped = entries.filter(e => e.action === 'skip').length;

    // Calculate total duration from first to last entry
    if (entries.length > 0) {
      const startTime = new Date(entries[0].timestamp).getTime();
      const endTime = new Date(entries[entries.length - 1].timestamp).getTime();
      const totalDurationMs = endTime - startTime;
      return { totalSteps, errors, skipped, totalDurationMs };
    }
    return { totalSteps, errors, skipped, totalDurationMs: 0 };
  };

  const handleCopy = async (text: string, field: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedField(field);
      setTimeout(() => setCopiedField(null), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const renderField = (label: string, value: string | null | undefined, copyable = false, monospace = false) => (
    <Box sx={{ mb: 2 }}>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
        {label}
      </Typography>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Typography
          variant="body1"
          sx={{
            fontFamily: monospace ? 'monospace' : 'inherit',
            bgcolor: monospace ? 'grey.100' : 'transparent',
            px: monospace ? 1 : 0,
            py: monospace ? 0.5 : 0,
            borderRadius: 1,
            wordBreak: 'break-all',
          }}
        >
          {value || '—'}
        </Typography>
        {copyable && value && (
          <Tooltip title={copiedField === label ? 'Copied!' : 'Copy'}>
            <IconButton
              size="small"
              onClick={() => handleCopy(value, label)}
              sx={{ color: copiedField === label ? 'success.main' : 'text.secondary' }}
            >
              <ContentCopy fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
      </Box>
    </Box>
  );

  if (isLoading) {
    return (
      <Container maxWidth="xl">
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  if (!log) {
    return (
      <Container maxWidth="xl">
        <Box sx={{ py: 4 }}>
          <Alert severity="error">
            Webhook log not found.
          </Alert>
        </Box>
      </Container>
    );
  }

  // Build raw JSON representation
  const rawJson = {
    id: log.id,
    event_id: log.eventId,
    event_type: log.eventType,
    realm_id: log.realmId,
    entity_name: log.entityName,
    entity_id: log.entityId,
    operation: log.operation,
    event_time: log.eventTime,
    payload: log.payload,
    processed: log.processed,
    processed_at: log.processedAt,
    error_message: log.errorMessage,
    created_at: log.createdAt,
    processing_status: log.processingStatus,
    trace_log: log.traceLog,
  };

  return (
    <Container maxWidth="xl">
      <Box sx={{ py: 4 }}>
        {/* Breadcrumbs */}
        <Breadcrumbs sx={{ mb: 3 }}>
          <Link
            color="inherit"
            href="/"
            onClick={(e) => {
              e.preventDefault();
              navigate('/');
            }}
            sx={{ display: 'flex', alignItems: 'center', textDecoration: 'none' }}
          >
            <Home sx={{ mr: 0.5 }} fontSize="inherit" />
            Home
          </Link>
          <Link
            color="inherit"
            href="/admin/hub"
            onClick={(e) => {
              e.preventDefault();
              navigate('/admin/hub');
            }}
            sx={{ display: 'flex', alignItems: 'center', textDecoration: 'none' }}
          >
            Admin Hub
          </Link>
          <Link
            color="inherit"
            href="/admin/quickbooks-webhook-logs"
            onClick={(e) => {
              e.preventDefault();
              navigate('/admin/quickbooks-webhook-logs');
            }}
            sx={{ display: 'flex', alignItems: 'center', textDecoration: 'none' }}
          >
            <Receipt sx={{ mr: 0.5 }} fontSize="inherit" />
            QuickBooks Webhook Logs
          </Link>
          <Typography color="text.primary" sx={{ display: 'flex', alignItems: 'center' }}>
            Log #{log.id}
          </Typography>
        </Breadcrumbs>

        {/* Back Button & Header */}
        <Box sx={{ mb: 4 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
            <Tooltip title="Back to Logs">
              <IconButton
                onClick={() => navigate('/admin/quickbooks-webhook-logs')}
                sx={{ color: '#607D8B' }}
              >
                <ArrowBack />
              </IconButton>
            </Tooltip>
            <Receipt sx={{ fontSize: 48, color: '#2CA01C' }} />
            <Box>
              <Typography variant="h4" component="h1" sx={{ color: '#607D8B' }}>
                Webhook Log #{log.id}
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                <Chip
                  label={log.entityName}
                  size="small"
                  variant="outlined"
                  sx={{ fontWeight: 500 }}
                />
                <Chip
                  label={log.operation}
                  size="small"
                  color={getOperationColor(log.operation) as any}
                />
                {log.processingStatus && (
                  <Chip
                    label={getProcessingStatusLabel(log.processingStatus)}
                    size="small"
                    color={getProcessingStatusColor(log.processingStatus) as any}
                    variant="outlined"
                  />
                )}
                <Typography variant="body2" color="text.secondary">
                  {formatDate(log.eventTime)}
                </Typography>
              </Box>
            </Box>
          </Box>
        </Box>

        {/* Main Content */}
        <Grid container spacing={3}>
          {/* Event Details */}
          <Grid item xs={12} md={6}>
            <Paper elevation={2} sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <Event sx={{ color: '#2CA01C' }} />
                <Typography variant="h6" sx={{ color: '#607D8B' }}>
                  Event Details
                </Typography>
              </Box>
              <Divider sx={{ mb: 2 }} />

              {renderField('Event ID', log.eventId, true, true)}
              {renderField('Event Type', log.eventType, false, true)}
              {renderField('Entity Name', log.entityName)}
              {renderField('Operation', log.operation)}
            </Paper>
          </Grid>

          {/* Entity Details */}
          <Grid item xs={12} md={6}>
            <Paper elevation={2} sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <Label sx={{ color: '#2CA01C' }} />
                <Typography variant="h6" sx={{ color: '#607D8B' }}>
                  Entity Details
                </Typography>
              </Box>
              <Divider sx={{ mb: 2 }} />

              {renderField('Entity ID', log.entityId, true, true)}
              {renderField('Realm ID', log.realmId, true, true)}
              {renderField('Processed', log.processed ? 'Yes' : 'No')}

              {log.errorMessage && (
                <Box sx={{ mt: 2 }}>
                  <Alert severity="error">
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      Error Message
                    </Typography>
                    <Typography variant="body2" sx={{ mt: 0.5 }}>
                      {log.errorMessage}
                    </Typography>
                  </Alert>
                </Box>
              )}
            </Paper>
          </Grid>

          {/* Timestamps */}
          <Grid item xs={12}>
            <Paper elevation={2} sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <Event sx={{ color: '#607D8B' }} />
                <Typography variant="h6" sx={{ color: '#607D8B' }}>
                  Timestamps
                </Typography>
              </Box>
              <Divider sx={{ mb: 2 }} />

              <Grid container spacing={3}>
                <Grid item xs={12} md={4}>
                  {renderField('Event Time (QuickBooks)', formatDate(log.eventTime))}
                </Grid>
                <Grid item xs={12} md={4}>
                  {renderField('Processed At', log.processedAt ? formatDate(log.processedAt) : '—')}
                </Grid>
                <Grid item xs={12} md={4}>
                  {renderField('Created At', formatDate(log.createdAt))}
                </Grid>
              </Grid>
            </Paper>
          </Grid>

          {/* Execution Trace */}
          {log.traceLog && (
            <Grid item xs={12}>
              <Paper elevation={2} sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Timeline sx={{ color: '#2CA01C' }} />
                    <Typography variant="h6" sx={{ color: '#607D8B' }}>
                      Execution Trace
                    </Typography>
                  </Box>
                  {(() => {
                    const summary = getTraceSummary(log.traceLog);
                    if (!summary) return null;
                    return (
                      <Box sx={{ display: 'flex', gap: 2 }}>
                        <Chip
                          label={`${summary.totalSteps} steps`}
                          size="small"
                          variant="outlined"
                        />
                        {summary.errors > 0 && (
                          <Chip
                            label={`${summary.errors} errors`}
                            size="small"
                            color="error"
                            variant="outlined"
                          />
                        )}
                        {summary.skipped > 0 && (
                          <Chip
                            label={`${summary.skipped} skipped`}
                            size="small"
                            color="warning"
                            variant="outlined"
                          />
                        )}
                        <Chip
                          label={formatDuration(summary.totalDurationMs)}
                          size="small"
                          variant="outlined"
                          sx={{ fontFamily: 'monospace' }}
                        />
                      </Box>
                    );
                  })()}
                </Box>
                <Divider sx={{ mb: 2 }} />

                {/* Trace ID */}
                <Box sx={{ mb: 2, p: 1.5, bgcolor: 'grey.50', borderRadius: 1 }}>
                  <Typography variant="caption" color="text.secondary">
                    Trace ID: <code>{log.traceLog.traceId}</code> |
                    Entity ID: <code>{log.traceLog.entityId}</code> |
                    Event: <code>{log.traceLog.eventType}</code>
                  </Typography>
                </Box>

                {/* Trace Entries */}
                <Box sx={{ position: 'relative' }}>
                  {log.traceLog.entries.map((entry, index) => (
                    <Box
                      key={index}
                      sx={{
                        display: 'flex',
                        mb: 1,
                        position: 'relative',
                        pl: 3,
                        '&::before': index < log.traceLog!.entries.length - 1 ? {
                          content: '""',
                          position: 'absolute',
                          left: 8,
                          top: 24,
                          bottom: -8,
                          width: 2,
                          bgcolor: 'grey.300',
                        } : {},
                      }}
                    >
                      {/* Icon */}
                      <Box
                        sx={{
                          position: 'absolute',
                          left: 0,
                          top: 2,
                          width: 20,
                          height: 20,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          bgcolor: 'background.paper',
                          zIndex: 1,
                        }}
                      >
                        {getTraceActionIcon(entry.action)}
                      </Box>

                      {/* Content */}
                      <Box sx={{ flex: 1 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                          <Typography
                            variant="body2"
                            sx={{
                              fontWeight: 500,
                              color: getTraceActionColor(entry.action),
                            }}
                          >
                            {entry.step}
                          </Typography>
                          {entry.durationMs !== undefined && (
                            <Typography
                              variant="caption"
                              sx={{ fontFamily: 'monospace', color: 'text.secondary' }}
                            >
                              ({formatDuration(entry.durationMs)})
                            </Typography>
                          )}
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ ml: 'auto' }}
                          >
                            {new Date(entry.timestamp).toLocaleTimeString()}
                          </Typography>
                        </Box>
                        <Typography variant="body2" color="text.secondary">
                          {entry.message}
                        </Typography>

                        {/* Data */}
                        {entry.data && Object.keys(entry.data).length > 0 && (
                          <Accordion
                            sx={{
                              mt: 1,
                              '&::before': { display: 'none' },
                              boxShadow: 'none',
                              bgcolor: 'grey.50',
                            }}
                          >
                            <AccordionSummary
                              expandIcon={<ExpandMore fontSize="small" />}
                              sx={{ minHeight: 32, py: 0 }}
                            >
                              <Typography variant="caption" color="text.secondary">
                                Show data ({Object.keys(entry.data).length} fields)
                              </Typography>
                            </AccordionSummary>
                            <AccordionDetails sx={{ pt: 0 }}>
                              <Box
                                component="pre"
                                sx={{
                                  m: 0,
                                  p: 1,
                                  bgcolor: 'grey.100',
                                  borderRadius: 1,
                                  fontSize: '0.75rem',
                                  fontFamily: 'monospace',
                                  overflow: 'auto',
                                  maxHeight: 150,
                                }}
                              >
                                {JSON.stringify(entry.data, null, 2)}
                              </Box>
                            </AccordionDetails>
                          </Accordion>
                        )}

                        {/* Error */}
                        {entry.error && (
                          <Alert severity="error" sx={{ mt: 1 }}>
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                              {entry.error.message}
                            </Typography>
                            {entry.error.stack && (
                              <Accordion
                                sx={{
                                  mt: 1,
                                  '&::before': { display: 'none' },
                                  boxShadow: 'none',
                                  bgcolor: 'transparent',
                                }}
                              >
                                <AccordionSummary
                                  expandIcon={<ExpandMore fontSize="small" />}
                                  sx={{ minHeight: 24, py: 0, px: 0 }}
                                >
                                  <Typography variant="caption">Show stack trace</Typography>
                                </AccordionSummary>
                                <AccordionDetails sx={{ p: 0 }}>
                                  <Box
                                    component="pre"
                                    sx={{
                                      m: 0,
                                      p: 1,
                                      bgcolor: 'rgba(0,0,0,0.1)',
                                      borderRadius: 1,
                                      fontSize: '0.7rem',
                                      fontFamily: 'monospace',
                                      overflow: 'auto',
                                      maxHeight: 150,
                                      whiteSpace: 'pre-wrap',
                                      wordBreak: 'break-word',
                                    }}
                                  >
                                    {entry.error.stack}
                                  </Box>
                                </AccordionDetails>
                              </Accordion>
                            )}
                          </Alert>
                        )}
                      </Box>
                    </Box>
                  ))}
                </Box>
              </Paper>
            </Grid>
          )}

          {/* Payload */}
          {log.payload && (
            <Grid item xs={12}>
              <Accordion>
                <AccordionSummary
                  expandIcon={<ExpandMore />}
                  sx={{ bgcolor: 'grey.50' }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Code sx={{ color: '#607D8B' }} />
                    <Typography variant="h6" sx={{ color: '#607D8B' }}>
                      Event Payload
                    </Typography>
                  </Box>
                </AccordionSummary>
                <AccordionDetails sx={{ p: 0 }}>
                  <Box sx={{ position: 'relative' }}>
                    <Tooltip title={copiedField === 'payload' ? 'Copied!' : 'Copy JSON'}>
                      <IconButton
                        size="small"
                        onClick={() => handleCopy(JSON.stringify(log.payload, null, 2), 'payload')}
                        sx={{
                          position: 'absolute',
                          top: 8,
                          right: 8,
                          color: copiedField === 'payload' ? 'success.main' : 'text.secondary',
                          bgcolor: 'background.paper',
                          '&:hover': { bgcolor: 'grey.200' },
                        }}
                      >
                        <ContentCopy fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Box
                      component="pre"
                      sx={{
                        bgcolor: 'grey.900',
                        color: 'grey.100',
                        p: 2,
                        m: 0,
                        overflow: 'auto',
                        fontFamily: 'monospace',
                        fontSize: '0.875rem',
                        lineHeight: 1.5,
                        borderRadius: '0 0 4px 4px',
                      }}
                    >
                      {JSON.stringify(log.payload, null, 2)}
                    </Box>
                  </Box>
                </AccordionDetails>
              </Accordion>
            </Grid>
          )}

          {/* Raw JSON */}
          <Grid item xs={12}>
            <Accordion>
              <AccordionSummary
                expandIcon={<ExpandMore />}
                sx={{ bgcolor: 'grey.50' }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Code sx={{ color: '#607D8B' }} />
                  <Typography variant="h6" sx={{ color: '#607D8B' }}>
                    Raw JSON Data
                  </Typography>
                </Box>
              </AccordionSummary>
              <AccordionDetails sx={{ p: 0 }}>
                <Box sx={{ position: 'relative' }}>
                  <Tooltip title={copiedField === 'rawJson' ? 'Copied!' : 'Copy JSON'}>
                    <IconButton
                      size="small"
                      onClick={() => handleCopy(JSON.stringify(rawJson, null, 2), 'rawJson')}
                      sx={{
                        position: 'absolute',
                        top: 8,
                        right: 8,
                        color: copiedField === 'rawJson' ? 'success.main' : 'text.secondary',
                        bgcolor: 'background.paper',
                        '&:hover': { bgcolor: 'grey.200' },
                      }}
                    >
                      <ContentCopy fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Box
                    component="pre"
                    sx={{
                      bgcolor: 'grey.900',
                      color: 'grey.100',
                      p: 2,
                      m: 0,
                      overflow: 'auto',
                      fontFamily: 'monospace',
                      fontSize: '0.875rem',
                      lineHeight: 1.5,
                      borderRadius: '0 0 4px 4px',
                    }}
                  >
                    {JSON.stringify(rawJson, null, 2)}
                  </Box>
                </Box>
              </AccordionDetails>
            </Accordion>
          </Grid>
        </Grid>
      </Box>
    </Container>
  );
};
